/**
 * DRPX Courier — Social-Hook: Favoriten & Blockieren
 *
 * Schreibt nur in das eigene Profil (kein Zugriff auf fremde Profile).
 *
 * @module hooks/useSocial
 */

import { useCallback } from 'react';
import { db } from '../lib/supabase.js';
import type { Profile, TranslateFn } from '../types.js';
import { istMitarbeiter } from '../lib/zugang.js';

type SetUsers = React.Dispatch<React.SetStateAction<Profile[]>>;
type SetCurrentUser = React.Dispatch<React.SetStateAction<Profile | null>>;
type ToastFn = (msg: string, type?: string) => void;
type OpenModalFn = (type: string, data?: unknown) => void;

interface UseSocialReturn {
  isBlocked: (otherId: string) => boolean;
  blockUser: (otherId: string, otherName?: string) => void;
  isFavorit: (fahrerId: string) => boolean;
  toggleFavorit: (fahrerId: string, fahrerName?: string) => Promise<void>;
}

export function useSocial(
  user: Profile | null,
  setUsers: SetUsers,
  setCurrentUser: SetCurrentUser,
  t: TranslateFn,
  openToast: ToastFn,
  onOpenModal: OpenModalFn,
): UseSocialReturn {
  const isBlocked = useCallback(
    function (otherId: string) {
      if (!user) return false;
      return (user.blockedUsers || []).some(function (b) {
        return b.userId === otherId;
      });
    },
    [user],
  );

  const blockUser = useCallback(
    function (otherId: string, otherName?: string) {
      // Rote Liste ist Chefsache (DRPX-Pro, 16.10.2026). `add_blocked` schreibt
      // ins Profil des Angemeldeten — beim Mitarbeiter wäre das sein eigenes,
      // nicht das der Firma. Der Knopf ist für ihn ausgeblendet; das hier ist
      // die zweite Sicherung.
      if (istMitarbeiter(user)) {
        openToast(t('nurChefHinweis'), 'info');
        return;
      }
      onOpenModal('confirm', {
        title: t('blockierenConfirm'),
        danger: true,
        onConfirm: async function () {
          if (!user) return;
          // B17: atomare RPC statt updateProfile(kompletteArray) — Race-sicher.
          // Fallback '' (siehe toggleFavorit): nie undefined an die RPC senden.
          const res = await db.addBlocked(otherId, otherName || '');
          if (res.error || !res.data) {
            openToast(t('hcApiFehler'), 'error');
            return;
          }
          const newBlocked = res.data;
          setUsers(function (prev) {
            return prev.map(function (u) {
              if (u.id === user.id) {
                return Object.assign({}, u, { blockedUsers: newBlocked });
              }
              return u;
            });
          });
          setCurrentUser(function (prev) {
            return prev ? Object.assign({}, prev, { blockedUsers: newBlocked }) : prev;
          });
          openToast(t('nutzerBlockiert'), 'info');
        },
      });
    },
    [user, t, openToast, setUsers, setCurrentUser, onOpenModal],
  );

  const isFavorit = useCallback(
    function (fahrerId: string) {
      if (!user) return false;
      return (user.favoriten || []).some(function (f) {
        return f.userId === fahrerId;
      });
    },
    [user],
  );

  const toggleFavorit = useCallback(
    async function (fahrerId: string, fahrerName?: string) {
      if (!user) return;
      const isFav = (user.favoriten || []).some(function (f) {
        return f.userId === fahrerId;
      });
      // B17: atomare RPC statt updateProfile(kompletteArray). Die DB liest +
      // schreibt das jsonb-Array in einer Anweisung → keine Race bei schnellen
      // Mehrfach-Klicks mehr. Rückgabe = das neue Array (echter Erfolgswert,
      // anders als beim alten apiSilent das immer null lieferte).
      // Fallback '' für den Namen: Supabase-JS lässt einen `undefined`-Parameter
      // beim Senden weg → dann käme nur p_fahrer_id an und PostgREST findet die
      // Zwei-Parameter-Funktion nicht (404). Mit '' wird p_name immer gesendet.
      const res = isFav
        ? await db.removeFavorit(fahrerId)
        : await db.addFavorit(fahrerId, fahrerName || '');
      if (res.error || !res.data) {
        openToast(t('hcApiFehler'), 'error');
        return;
      }
      const newFavoriten = res.data;
      setUsers(function (prev) {
        return prev.map(function (u) {
          if (u.id !== user.id) return u;
          return Object.assign({}, u, { favoriten: newFavoriten });
        });
      });
      // Auch das eigene Profil (currentUser) aktualisieren — die Favoriten-
      // Seite liest user.favoriten aus currentUser, nicht aus dem users-Array.
      setCurrentUser(function (prev) {
        return prev ? Object.assign({}, prev, { favoriten: newFavoriten }) : prev;
      });
      openToast(
        isFav ? t('ausFavoritenEntfernt') : t('zuFavoritenHinzu'),
        isFav ? 'info' : 'success',
      );
    },
    [user, t, openToast, setUsers, setCurrentUser],
  );

  return { isBlocked, blockUser, isFavorit, toggleFavorit };
}
